import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import moment from "moment-timezone"
import { defaultsDeep } from "lodash"
import { Symbol } from "../constants"
import { Minus } from "../constants/symbols"
import { unitGroups } from "./units"
import type { FormatPercentageParameters } from "./formatters/priceFormatters"
import { padDecimals } from "./formatters/priceFormatters"
import { cateringTypeLabels, isCateringType } from "../components/screens/categories/utils"

export const firstLetterUpperCase = (s: Maybe<string>) => {
    if (!s) {
        return ""
    }

    return s.charAt(0).toUpperCase() + s.slice(1)
}

export const getUnitBasedPrice = (price: Maybe<number>): number => {
    if (!price) {
        return 0
    }

    return price / 1000
}
export const formatPercentage = (percentage: Maybe<number>, _options: Maybe<FormatPercentageParameters>): string => {
    const options = defaultsDeep(_options, {
        withPlus: false,
        zeroIsNull: false,
        alwaysDecimal: false,
        places: 3,
    })

    if (percentage == null || (options.zeroIsNull && percentage === 0)) {
        return Symbol.MDash
    }

    let sign = ""

    if (percentage < 0) {
        sign = Symbol.Minus
    } else if (percentage > 0 && options.withPlus) {
        sign = Symbol.Plus
    }

    const exp = Math.pow(10, options.places)

    const rounded = Math.abs(Math.round((percentage / 1000) * exp) / exp)
    const expanded = options.alwaysDecimal ? padDecimals(rounded, options.places) : rounded
    return `${sign}${expanded}%`
}

export const formatZip = (s: string) => {
    const s2 = ("" + s).replace(/\D/g, "")
    const m = s2.match(/(\w{5})/)
    return !m ? null : m[0]
}
export const formatDateTime = (date: Date | string, formatting: string, timezone?: Maybe<string>): string => {
    return timezone ? moment.tz(date, timezone).format(formatting) : moment(date).local().format(formatting)
}

export const formatTimeDuration = (durationInSeconds: number): string => {
    const seconds = durationInSeconds % 60
    const minutes = Math.floor(durationInSeconds / 60) % 60
    const hours = Math.floor(durationInSeconds / 3600)
    const parts: string[] = []

    if (hours > 0) {
        parts.push(`${hours}h`)
    }

    if (minutes > 0) {
        parts.push(`${minutes}m`)
    }

    const shouldDisplaySeconds: boolean = (hours === 0 && minutes === 0) || (hours === 0 && minutes > 0 && seconds > 0)

    if (shouldDisplaySeconds) {
        parts.push(`${seconds}s`)
    }

    return parts.join(" ")
}

export const formatUnitBasedPrice = (price: Maybe<number>, unit: string): Maybe<string> => {
    const groups = unitGroups

    if (!price) {
        return null
    }

    let symbol = null

    if (isCateringType(unit)) {
        symbol = cateringTypeLabels[unit]
    } else {
        const mark = groups.find(g => g.units[unit])

        if (!mark) {
            return null
        }

        // @ts-expect-error
        symbol = mark.units[unit].symbol
    }

    // @ts-expect-error
    return symbol ? `${formatAsPrice(price)}/${symbol.toLowerCase()}` : null
}
export const formatAsPrice = (
    inputNum: number | number[],
    inputConfig: Record<string, any> = {},
    currency: string = "$"
): string => {
    if (Array.isArray(inputNum) && inputNum.length === 2) {
        // show as range
        const rangeStart = format(inputNum[0], inputConfig, currency)
        const rangeEnd = format(inputNum[1], inputConfig, currency)
        return `${rangeStart}–${rangeEnd}`
    }

    return format(inputNum, inputConfig, currency)
}

export const format = (inputNum, inputConfig, currency): string => {
    let num = inputNum
    const config = inputConfig || {}
    const displayNumberOnly = config.numberOnly || false
    const withoutDecimals = config.withoutDecimals || false
    let sign = ""

    if (num < 0) {
        num = Math.abs(num)
        sign = Minus
    }

    let result = sign

    if (!displayNumberOnly) {
        result += currency === "$" ? "$" : "Kč"
    }

    if (withoutDecimals) {
        if (currency === "$") {
            result += Math.round(num / 100)
        } else {
            result = Math.round(num / 100) + result
        }
    } else {
        let temp = ""

        if (currency === "$") {
            temp = result + (num / 100).toFixed(2)
        } else {
            // Add not breaking space - char code 160
            temp = (num / 100).toFixed(2) + String.fromCharCode(160) + result
        }

        result = temp
    }

    return addThousandSeparators(result)
}

const addThousandSeparators = (x): string => {
    // See http://stackoverflow.com/a/2901298/875523
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")
}

export const numberToDollarsFormatter = (value: number) => `$${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")

export const numberToDollarsParser = (value: string) => value.replace(/\$\s?|(,*)/g, "")

export const PaymentStateEnum = {
    unpaid: "unpaid",
    partiallyPaid: "partially-paid",
    paid: "paid",
    overcharged: "overcharged",
    partiallyRefunded: "partially-refunded",
    refunded: "refunded",
    forfeited: "forfeited",
    voided: "voided",
}

export type PaymentState = (typeof PaymentStateEnum)[keyof typeof PaymentStateEnum]

export const formatPaymentState = (state: Maybe<PaymentState>): Maybe<string> => {
    switch (state) {
        case PaymentStateEnum.unpaid:
            return "Unpaid"

        case PaymentStateEnum.partiallyPaid:
            return "Partially Paid"

        case PaymentStateEnum.paid:
            return "Paid"

        case PaymentStateEnum.overcharged:
            return "Overcharged"

        case PaymentStateEnum.partiallyRefunded:
            return "Partial Refund"

        case PaymentStateEnum.refunded:
            return "Refund"

        case PaymentStateEnum.forfeited:
            return "Forfeited"

        case PaymentStateEnum.voided:
            return "Voided"

        default:
            return null
    }
}
